/**
 * The activity timeline (`GET /activity/v1`), shared by the Activity page and a machine's Activity view: day by day,
 * each run one plain sentence that opens its history, and between the runs the quieter things that were not runs
 * (an approval asked or answered, an agent's error, a team started, a task done).
 */

import {
  ACTIVITY_STATUS_WORDS,
  activityDayKey,
  activityDayLabel,
  activityRunFacts,
  activityRunSentence,
  activityTime,
  foldedSentence,
  type ActivityEventItem,
  type ActivityItem,
  type ActivityRunItem,
  type HistoryRunStatus,
} from '@aioe/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { StatusMark } from './ui.tsx';

const RUN_MARK: Record<HistoryRunStatus, 'working' | 'connected' | 'error' | 'idle'> = { running: 'working', ok: 'connected', error: 'error', cancelled: 'idle' };
const RUN_WORDS: Record<HistoryRunStatus, string> = { ...ACTIVITY_STATUS_WORDS, cancelled: 'Stopped' };

const runLink = (r: ActivityRunItem) => `/projects/${encodeURIComponent(r.projectId)}?tab=overview&part=history&run=${encodeURIComponent(r.id)}`;

/** A session on a machine, live: the machine's page with its Sessions view and that session open. */
export const liveLink = (deviceId: string, session?: string) => `/workbenches/${encodeURIComponent(deviceId)}?view=sessions${session ? `&session=${encodeURIComponent(`s:${session}`)}` : ''}`;

/** Day by day. `showMachine: false` on a machine's own page, where every row is on that machine. */
export function Timeline({ items, now, showProject, showMachine = true }: { items: ActivityItem[]; now: number; showProject: boolean; showMachine?: boolean }) {
  const days: Array<{ key: string; label: string; items: ActivityItem[] }> = [];
  for (const it of items) {
    const key = activityDayKey(it.at);
    const last = days.at(-1);
    if (last?.key === key) last.items.push(it);
    else days.push({ key, label: activityDayLabel(it.at, now), items: [it] });
  }
  return (
    <div>
      {days.map((d) => {
        const runs = d.items.filter((i) => i.type === 'run').length;
        return (
          <section key={d.key} aria-label={d.label} className="border-t border-separator first:border-t-0">
            <h3 className="flex items-baseline gap-2 px-4 pt-3 pb-1 text-xs font-semibold">
              {d.label}
              <span className="font-normal text-muted tabular-nums">{runs === 1 ? '1 run' : `${runs.toLocaleString()} runs`}</span>
            </h3>
            <ol className="pb-1.5">
              {d.items.map((it) => (it.type === 'run' ? <RunRow key={`run/${it.projectId}/${it.rootId}/${it.id}`} r={it} now={now} showProject={showProject} /> : <EventRow key={it.id} e={it} showProject={showProject} showMachine={showMachine} />))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

/**
 * One line of the timeline: the time in a narrow column of its own, the words, and the state at the
 * trailing end. In a column narrower than 35rem the state goes under the words instead.
 */
function Line({ at, children, mark, quiet }: { at: string; children: ReactNode; mark?: ReactNode; quiet?: boolean }) {
  return (
    <li className={`flex items-start gap-3 px-4 py-1.5 ${quiet ? 'text-muted' : ''}`}>
      <span className="w-14 shrink-0 pt-px text-xs text-muted tabular-nums">{activityTime(at)}</span>
      <div className="min-w-0 flex-1">
        {children}
        {mark ? <div className="mt-0.5 @[35rem]:hidden">{mark}</div> : null}
      </div>
      {mark ? <span className="hidden shrink-0 @[35rem]:inline-flex">{mark}</span> : null}
    </li>
  );
}

function RunRow({ r, now, showProject }: { r: ActivityRunItem; now: number; showProject: boolean }) {
  const facts = activityRunFacts(r, now, { project: showProject });
  if (r.person) facts.push(r.person.name);
  return (
    <Line at={r.at} mark={<StatusMark state={RUN_MARK[r.status]} label={RUN_WORDS[r.status]} />}>
      <Link to={runLink(r)} className="text-sm font-medium hover:underline" title="Open this run's history">
        {activityRunSentence(r)}
      </Link>
      <div className="line-clamp-2 text-xs text-muted tabular-nums">
        {facts.join(' · ')}
        {r.status === 'running' && r.machine && r.sessionId ? (
          <>
            {' · '}
            <Link to={liveLink(r.machine.deviceId, r.sessionId)} className="text-accent hover:underline">
              Open live
            </Link>
          </>
        ) : null}
      </div>
    </Line>
  );
}

function EventRow({ e, showProject, showMachine }: { e: ActivityEventItem; showProject: boolean; showMachine: boolean }) {
  const where = [showProject ? e.projectName : undefined, showMachine && e.machine ? `on ${e.machine.name}` : undefined].filter(Boolean).join(' · ');
  const text = e.count && e.count > 1 && e.firstAt ? foldedSentence({ sentence: e.text, count: e.count, at: Date.parse(e.at), firstAt: Date.parse(e.firstAt) }) : e.text;
  return (
    <Line at={e.at} quiet mark={e.open ? <StatusMark state="medium" label="Waiting" /> : undefined}>
      <div className="text-sm">
        <span className={e.kind === 'agent.error' ? 'text-danger' : undefined}>{text}</span>
        {where ? <span className="text-xs"> · {where}</span> : null}
        {e.open && e.live ? (
          <>
            {' · '}
            <Link to={liveLink(e.live.deviceId, e.live.sessionId)} className="text-xs text-accent hover:underline">
              Open live
            </Link>
          </>
        ) : null}
      </div>
    </Line>
  );
}
